<script lang="ts">
  import { link, push } from "svelte-spa-router";
  import { resetPassword, ApiError } from "../lib/api";

  /** Route params injected by svelte-spa-router: { token }. */
  export let params: { token?: string } = {};

  let password = "";
  let confirm = "";
  let submitting = false;
  let error: string | null = null;
  let done = false;

  async function submit(e: Event) {
    e.preventDefault();
    error = null;
    if (!password || password.length < 1) {
      error = "Password required.";
      return;
    }
    if (password !== confirm) {
      error = "Passwords do not match.";
      return;
    }
    const token = params.token ?? "";
    if (!token) {
      error = "Invalid reset link.";
      return;
    }
    submitting = true;
    try {
      await resetPassword(token, { password });
      done = true;
      setTimeout(() => push("/"), 1500);
    } catch (err) {
      error =
        err instanceof ApiError
          ? err.message === "invalid_or_expired_token"
            ? "This reset link is invalid or has expired."
            : err.message
          : "Reset failed.";
    } finally {
      submitting = false;
    }
  }
</script>

<section class="reset">
  <div class="card">
    <h1>Set a new password</h1>
    {#if done}
      <p class="sub" data-testid="reset-done">
        Your password has been reset. Redirecting to sign in…
      </p>
    {:else}
      <form on:submit={submit}>
        <label>
          New password
          <input
            type="password"
            autocomplete="new-password"
            bind:value={password}
            data-testid="new-password"
            required
          />
        </label>
        <label>
          Confirm password
          <input
            type="password"
            autocomplete="new-password"
            bind:value={confirm}
            data-testid="confirm-password"
            required
          />
        </label>
        <button type="submit" disabled={submitting} data-testid="reset-submit">
          {submitting ? "Saving…" : "Set new password"}
        </button>
        {#if error}
          <p class="error" data-testid="reset-error">{error}</p>
        {/if}
      </form>
      <a href="/" use:link>← Back to sign in</a>
    {/if}
  </div>
</section>

<style>
  .reset {
    min-height: 60vh;
    display: grid;
    place-items: center;
    padding: 1rem;
  }
  .card {
    width: 100%;
    max-width: 420px;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 12px;
    padding: 1.5rem;
  }
  h1 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
    line-height: 1.5;
  }
  form {
    display: grid;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  input {
    padding: 0.55rem 0.7rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 6px;
  }
  button {
    padding: 0.55rem 0.7rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 6px;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.85rem;
  }
  a {
    color: #34d399;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
</style>
